// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	createLivekitParticipantSnapshot,
	createLivekitParticipantSnapshotsFromRoom,
	createVoiceParticipantSnapshot,
	extractParticipantConnectionId,
	extractParticipantUserId,
	findParticipantSnapshotByUserIdAndConnectionId,
	type LivekitParticipantSnapshot,
	transitionVoiceParticipantSnapshot,
	type VoiceParticipantEvent,
	type VoiceParticipantSnapshot,
} from '@app/features/voice/engine/VoiceParticipantStateMachine';
import {
	VoiceConnectionQuality,
	type VoiceConnectionQuality as VoiceConnectionQualityType,
} from '@app/features/voice/engine/VoiceTrackSource';
import type {Participant, Room} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceParticipantManager');

export type {LivekitParticipantSnapshot} from '@app/features/voice/engine/VoiceParticipantStateMachine';

export function isParticipantSnapshotSpeaking(snapshot: LivekitParticipantSnapshot | undefined): boolean {
	return Boolean(snapshot && (snapshot.isSpeaking || snapshot.isAudioLevelSpeaking));
}

class VoiceParticipantManager {
	private machineSnapshot: VoiceParticipantSnapshot = createVoiceParticipantSnapshot();
	private _participants: Readonly<Record<string, LivekitParticipantSnapshot>> =
		this.machineSnapshot.context.participants;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get participants(): Readonly<Record<string, LivekitParticipantSnapshot>> {
		return this._participants;
	}

	private transition(event: VoiceParticipantEvent): boolean {
		const nextSnapshot = transitionVoiceParticipantSnapshot(this.machineSnapshot, event);
		if (nextSnapshot.context.participants === this._participants) {
			this.machineSnapshot = nextSnapshot;
			return false;
		}
		runInAction(() => {
			this.machineSnapshot = nextSnapshot;
			this._participants = nextSnapshot.context.participants;
		});
		return true;
	}

	upsertParticipant(participant: Participant): void {
		const existing = this._participants[participant.identity];
		const snapshot = createLivekitParticipantSnapshot(participant, existing);
		if (this.transition({type: 'participant.upsert', snapshot})) {
			logger.debug('Updated', {identity: participant.identity, isLocal: participant.isLocal});
		}
	}

	setAudioLevelSpeaking(identity: string, speaking: boolean): void {
		this.transition({type: 'participant.setAudioLevelSpeaking', identity, speaking});
	}

	removeParticipant(identity: string): void {
		if (this.transition({type: 'participant.remove', identity})) {
			logger.debug('Removed', {identity});
		}
	}

	hydrateFromRoom(room: Room): void {
		const snapshots = createLivekitParticipantSnapshotsFromRoom(room, this._participants);
		this.transition({type: 'participant.hydrate', snapshots});
		logger.info('Hydrated participants', {count: snapshots.length});
	}

	updateActiveSpeakers(speakers: Array<Participant>): void {
		this.transition({type: 'participant.activeSpeakers', identities: speakers.map((s) => s.identity)});
	}

	upsertParticipantFromNative(fields: {
		identity: string;
		sid: string;
		name?: string;
		isLocal?: boolean;
		metadata?: string;
		attributes?: Readonly<Record<string, string>>;
		isMicrophoneEnabled?: boolean;
		isCameraEnabled?: boolean;
		isScreenShareEnabled?: boolean;
	}): void {
		const existing = this._participants[fields.identity];
		const snapshot: LivekitParticipantSnapshot = Object.freeze({
			identity: fields.identity,
			userId: extractParticipantUserId(fields.identity),
			connectionId: extractParticipantConnectionId(fields.identity),
			sid: fields.sid || existing?.sid || '',
			isLocal: fields.isLocal ?? existing?.isLocal ?? false,
			isSpeaking: existing?.isSpeaking ?? false,
			isAudioLevelSpeaking: existing?.isAudioLevelSpeaking ?? false,
			connectionQuality: existing?.connectionQuality ?? VoiceConnectionQuality.Unknown,
			metadata: fields.metadata ?? existing?.metadata,
			attributes: fields.attributes ?? existing?.attributes ?? Object.freeze({}),
			audioTrackSids: existing?.audioTrackSids ?? Object.freeze([]),
			videoTrackSids: existing?.videoTrackSids ?? Object.freeze([]),
			isMicrophoneEnabled: fields.isMicrophoneEnabled ?? existing?.isMicrophoneEnabled ?? false,
			isCameraEnabled: fields.isCameraEnabled ?? existing?.isCameraEnabled ?? false,
			isScreenShareEnabled: fields.isScreenShareEnabled ?? existing?.isScreenShareEnabled ?? false,
			joinedAt: existing?.joinedAt ?? Date.now(),
			lastSpokeAt: existing?.lastSpokeAt ?? null,
		});
		this.transition({type: 'participant.upsert', snapshot});
	}

	patchParticipantTrackFlags(
		identity: string,
		flags: {isMicrophoneEnabled?: boolean; isCameraEnabled?: boolean; isScreenShareEnabled?: boolean},
	): void {
		const existing = this._participants[identity];
		if (!existing) return;
		const snapshot: LivekitParticipantSnapshot = Object.freeze({
			...existing,
			isMicrophoneEnabled: flags.isMicrophoneEnabled ?? existing.isMicrophoneEnabled,
			isCameraEnabled: flags.isCameraEnabled ?? existing.isCameraEnabled,
			isScreenShareEnabled: flags.isScreenShareEnabled ?? existing.isScreenShareEnabled,
		});
		this.transition({type: 'participant.upsert', snapshot});
	}

	setConnectionQualityForNative(sid: string, quality: VoiceConnectionQualityType): void {
		for (const existing of Object.values(this._participants)) {
			if (existing.sid !== sid) continue;
			if (existing.connectionQuality === quality) return;
			this.transition({type: 'participant.upsert', snapshot: Object.freeze({...existing, connectionQuality: quality})});
			return;
		}
	}

	updateActiveSpeakersBySid(sids: ReadonlyArray<string>): void {
		const speakerSidSet = new Set(sids);
		const speakerIdentities: Array<string> = [];
		for (const participant of Object.values(this._participants)) {
			if (speakerSidSet.has(participant.sid)) {
				speakerIdentities.push(participant.identity);
			}
		}
		this.transition({type: 'participant.activeSpeakers', identities: speakerIdentities});
	}

	removeParticipantBySid(sid: string): void {
		for (const participant of Object.values(this._participants)) {
			if (participant.sid === sid) {
				this.removeParticipant(participant.identity);
				return;
			}
		}
	}

	getParticipantByUserIdAndConnectionId(
		userId: string,
		connectionId: string | null,
	): LivekitParticipantSnapshot | undefined {
		return findParticipantSnapshotByUserIdAndConnectionId(this._participants, userId, connectionId);
	}

	getParticipant(identity: string): LivekitParticipantSnapshot | undefined {
		return this._participants[identity];
	}

	getLocalParticipant(): LivekitParticipantSnapshot | undefined {
		for (const participant of Object.values(this._participants)) {
			if (participant.isLocal) {
				return participant;
			}
		}
		return undefined;
	}

	clear(): void {
		if (this.transition({type: 'participant.clear'})) {
			logger.debug('All participants cleared');
		}
	}

	extractUserId(identity: string): string | null {
		return extractParticipantUserId(identity);
	}

	extractConnectionId(identity: string): string | null {
		return extractParticipantConnectionId(identity);
	}
}

const instance = new VoiceParticipantManager();

(
	window as typeof window & {
		_voiceParticipantManager?: VoiceParticipantManager;
	}
)._voiceParticipantManager = instance;

export default instance;
